import axios from "@/config/axios";
import router from "@/router";

import {
  AuthenticationApi,
  AuthorizedResultDto,
  LoginWithCredentialsRequest,
  RefreshAccessTokenRequest
} from "@/api";

const api = new AuthenticationApi();

// administrator is obsolete
interface AuthState {
  auth: AuthorizedResultDto | null;
  status: {
    isAdministrator: boolean;
    isManager: boolean;
    loggedIn: boolean;
  };
}

let initialState: AuthState;
if (localStorage.getItem("spits-mijn-user")) {
  initialState = JSON.parse(localStorage.getItem("spits-mijn-user") || "");
} else {
  initialState = {
    status: { isAdministrator: false, isManager: false, loggedIn: false },
    auth: null
  };
}

export const auth = {
  namespaced: true,
  state: initialState,
  actions: {
    async login(
      { commit, dispatch },
      payload: {
        credentials: LoginWithCredentialsRequest;
        code?: string;
      }
    ) {
      try {
        const call = payload.code
          ? api.authenticationLoginWithTotp({
              pin: payload.code,
              ...payload.credentials
            })
          : api.authenticationLoginWithCredentials(payload.credentials);

        const { data } = await call;

        const state: AuthState = {
          auth: data,
          status: {
            loggedIn: true,
            isAdministrator: data.userInfo?.isAdministrator === true,
            isManager: data.userInfo?.isManager === true
          }
        };
        axios.setHeader(state.auth?.accessToken);
        localStorage.setItem("spits-mijn-user", JSON.stringify(state));
        commit("loginSuccess", state);
      } catch (e) {
        if (e.response.status === 409) {
          commit("loginTokenRequested");
        } else {
          commit("loginFailure");
        }
      }
    },
    async refresh({ commit, dispatch, state }) {
      try {
        const request: RefreshAccessTokenRequest = {
          accessToken: state.auth.accessToken,
          refreshToken: state.auth.refreshToken
        };
        const { data } = await api.authenticationRefreshAccessToken(request, {
          transformRequest: (data, headers) => {
            delete headers.common["Authorization"];
            return data;
          }
        });
        state.auth.accessToken = data.accessToken;
        state.auth.refreshToken = data.refreshToken;
        localStorage.setItem("spits-mijn-user", JSON.stringify(state));

        axios.setHeader(data.accessToken);
        commit("loginSuccess", state);
      } catch (e) {
        localStorage.removeItem("spits-mijn-user");
        commit("logout");
        router.push("/auth");
      }
    },
    async logout({ commit }) {
      try {
        await api.authenticationLogout();
      } finally {
        axios.reset();
        localStorage.removeItem("spits-mijn-user");
        commit("logout");
        router.push("/auth");
      }
    },
    setAuth({ commit, state }, authorizedResultDto: AuthorizedResultDto) {
      state.auth.accessToken = authorizedResultDto.accessToken;
      state.auth.refreshToken = authorizedResultDto.refreshToken;

      axios.setHeader(state.auth?.accessToken);
      commit("loginSuccess", state);
    }
  },
  mutations: {
    loginRequest(state, user) {
      state.status = { loggingIn: true, loginFailed: false };
      state.user = user;
    },
    loginSuccess(state: AuthState, payload: AuthState) {
      state.auth = payload.auth;
      state.status = payload.status;
    },
    loginTokenRequested(state: AuthState) {
      state.auth = null;
    },
    loginFailure(state) {
      state.status = { loggingIn: true, loginFailed: true };
      state.user = null;
    },
    logout(state: AuthState) {
      state.status = {
        loggedIn: false,
        isAdministrator: false,
        isManager: false
      };
      state.auth = null;
    }
  },
  getters: {
    isLoggedIn: (state: AuthState) => state.status?.loggedIn === true,
    isAdmin: (state: AuthState) => state.status?.isAdministrator === true,
    isManager: (state: AuthState) => state.status?.isManager === true,
    getAuth: (state: AuthState) => state.auth
  }
};
